import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@clerk/react';
import {
  Container,
  Grid,
  Card,
  Stack,
  Text,
  Title,
  Group,
  Badge,
  Button,
  Loader,
  Alert,
  Divider,
  ActionIcon,
  NumberInput,
  Breadcrumbs,
  Anchor,
  Image,
  Tabs
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconArrowLeft,
  IconShoppingCart,
  IconHeart,
  IconShare,
  IconUser,
  IconMapPin,
  IconTruck,
  IconShield,
  IconEye,
  IconStar
} from '@tabler/icons-react';
import { useApi, useLiveQuery, mediaUrl } from '../api';
import { addToCart, selectCartItemById } from '../store/slices/cartSlice';
import { formatCents, formatCondition, getConditionColor } from '../components/products/productDisplay';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const { isSignedIn } = useAuth();
  const cartItem = useSelector(state => selectCartItemById(state, parseInt(productId)));

  const { data: product, loading, error } = useLiveQuery(
    `/api/v1/products/${productId}`,
    undefined,
    { topics: [`products.${productId}`] }
  );

  // Seller card data (public schema §3.5) — the product payload only carries
  // seller_id + seller_name
  const { data: seller } = useApi(
    `/api/v1/sellers/${product?.seller_id ?? 0}`,
    undefined,
    { enabled: !!product }
  );

  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);

  const handleAddToCart = async () => {
    if (!product) return;

    setAddingToCart(true);
    try {
      dispatch(addToCart({
        product: product,
        quantity: quantity
      }));

      console.log(`Added ${quantity} x ${product.title} to cart`);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleToggleFavorite = async () => {
    // TODO: Implement favorites functionality
    console.log('Toggle favorite:', productId);
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: product.title,
        text: product.description,
        url: window.location.href,
      });
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (error) {
    if (error.code === 'not_found') {
      return <Navigate to="/products" replace />;
    }
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconExclamationCircle size="1rem" />}
          color="red"
          title="Error Loading Product"
        >
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: '/' },
    { title: 'Products', href: '/products' },
    { title: product.title, href: '#' }
  ].map((item, index) => (
    <Anchor component={Link} to={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  const productImages = product.images ?? [];
  const sellerLocation = seller
    ? [seller.business_city, seller.business_state].filter(Boolean).join(', ')
    : '';

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Breadcrumbs */}
        <Group>
          <ActionIcon component={Link} to="/products" variant="light" size="lg">
            <IconArrowLeft size="1.2rem" />
          </ActionIcon>
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Group>

        {/* Main Product Information */}
        <Grid>
          {/* Product Images */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              {/* Main Image */}
              <Card shadow="sm" padding={0} radius="md" withBorder>
                <div style={{ position: 'relative', height: 400, backgroundColor: '#f8f9fa' }}>
                  {productImages.length > 0 ? (
                    <Image
                      src={mediaUrl(productImages[selectedImageIndex]?.image_path)}
                      alt={productImages[selectedImageIndex]?.alt_text || product.title}
                      fit="contain"
                      height={400}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <Text c="dimmed" size="lg">No Image Available</Text>
                    </div>
                  )}

                  {/* Views Counter */}
                  <Group
                    gap="xs"
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      padding: '4px 8px',
                      borderRadius: 4
                    }}
                  >
                    <IconEye size="1rem" color="white" />
                    <Text size="sm" c="white">{product.views}</Text>
                  </Group>
                </div>
              </Card>

              {/* Thumbnail Images */}
              {productImages.length > 1 && (
                <Group gap="xs">
                  {productImages.map((image, index) => (
                    <Card
                      key={image.id ?? index}
                      shadow="sm"
                      padding={0}
                      radius="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        opacity: selectedImageIndex === index ? 1 : 0.6,
                        border: selectedImageIndex === index ? '2px solid var(--mantine-color-pink-6)' : undefined
                      }}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <Image
                        src={mediaUrl(image.image_path)}
                        alt={image.alt_text || `${product.title} ${index + 1}`}
                        fit="cover"
                        height={80}
                        width={80}
                      />
                    </Card>
                  ))}
                </Group>
              )}
            </Stack>
          </Grid.Col>

          {/* Product Details */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="lg">
              {/* Title and Price */}
              <div>
                <Title order={1} mb="sm">{product.title}</Title>
                <Group gap="md" align="center">
                  <Text size="xl" fw={700} c="pink">{formatCents(product.price_cents)}</Text>
                  <Badge color={getConditionColor(product.condition)} variant="light">
                    {formatCondition(product.condition)}
                  </Badge>
                  {product.quantity === 0 && (
                    <Badge color="red" variant="light">
                      Out of Stock
                    </Badge>
                  )}
                </Group>
                {product.tags && product.tags.length > 0 && (
                  <Group gap="xs" mt="sm">
                    {product.tags.map((tag) => (
                      <Badge key={tag} color="grape" variant="light" size="sm">{tag}</Badge>
                    ))}
                  </Group>
                )}
              </div>

              {/* Seller Information */}
              <Card shadow="sm" padding="md" radius="md" withBorder>
                <Group gap="md">
                  <IconUser size="2rem" color="var(--mantine-color-gray-6)" />
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" align="center">
                      <Text fw={500}>{product.seller_name}</Text>
                      {seller?.is_verified && (
                        <Badge color="green" variant="light" size="xs">
                          Verified Princess
                        </Badge>
                      )}
                    </Group>
                    {sellerLocation && (
                      <Group gap="xs" mt="xs">
                        <IconMapPin size="0.9rem" color="var(--mantine-color-gray-6)" />
                        <Text size="sm" c="dimmed">{sellerLocation}</Text>
                      </Group>
                    )}
                    {seller?.average_rating > 0 && (
                      <Group gap="xs" mt="xs">
                        <IconStar size="0.9rem" color="var(--mantine-color-yellow-6)" />
                        <Text size="sm">
                          {seller.average_rating}/5 ({seller.total_reviews} reviews)
                        </Text>
                      </Group>
                    )}
                  </div>
                </Group>
              </Card>

              {/* Quantity and Actions */}
              <Stack gap="md">
                {product.quantity > 0 && (
                  <Group gap="md">
                    <Text fw={500}>Quantity:</Text>
                    <NumberInput
                      value={quantity}
                      onChange={setQuantity}
                      min={1}
                      max={product.quantity}
                      style={{ width: 100 }}
                    />
                    <Text size="sm" c="dimmed">
                      {product.quantity} available
                    </Text>
                  </Group>
                )}

                <Group gap="md">
                  <Button
                    leftSection={<IconShoppingCart size="1rem" />}
                    size="lg"
                    disabled={product.quantity === 0 || addingToCart}
                    onClick={handleAddToCart}
                    loading={addingToCart}
                    style={{ flex: 1 }}
                  >
                    {product.quantity === 0 ? 'Out of Stock' :
                     cartItem ? `Update Cart (${cartItem.quantity} in cart)` : 'Add to Cart'}
                  </Button>

                  <ActionIcon
                    size="lg"
                    variant="light"
                    onClick={handleToggleFavorite}
                    disabled={!isSignedIn}
                  >
                    <IconHeart size="1.2rem" />
                  </ActionIcon>

                  <ActionIcon
                    size="lg"
                    variant="light"
                    onClick={handleShare}
                  >
                    <IconShare size="1.2rem" />
                  </ActionIcon>
                </Group>
              </Stack>

              {/* Product Features */}
              <Stack gap="xs">
                <Group gap="xs">
                  <IconShield size="1rem" color="var(--mantine-color-green-6)" />
                  <Text size="sm">Verified Princess Guarantee</Text>
                </Group>
                <Group gap="xs">
                  <IconTruck size="1rem" color="var(--mantine-color-pink-6)" />
                  <Text size="sm">Fast & Secure Delivery</Text>
                </Group>
              </Stack>
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Product Details Tabs */}
        <Tabs defaultValue="description" variant="outline">
          <Tabs.List>
            <Tabs.Tab value="description">Description</Tabs.Tab>
            <Tabs.Tab value="specifications">Specifications</Tabs.Tab>
            <Tabs.Tab value="shipping">Shipping & Returns</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="description" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Product Description</Title>
              <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {product.description || 'No description available.'}
              </Text>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="specifications" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Specifications</Title>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={500}>Condition:</Text>
                  <Text>{formatCondition(product.condition)}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Brand:</Text>
                  <Text>{product.brand || 'Not specified'}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Model:</Text>
                  <Text>{product.model || 'Not specified'}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Size:</Text>
                  <Text>{product.size || 'Not specified'}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Color:</Text>
                  <Text>{product.color || 'Not specified'}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Listed:</Text>
                  <Text>{new Date(product.created_at).toLocaleDateString()}</Text>
                </Group>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="shipping" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Shipping & Returns</Title>
              <Stack gap="md">
                <div>
                  <Text fw={500} mb="xs">Shipping Options:</Text>
                  <Text size="sm" c="dimmed">
                    Standard shipping typically takes 3-7 business days. Express shipping options may be available.
                  </Text>
                </div>
                <Divider />
                <div>
                  <Text fw={500} mb="xs">Return Policy:</Text>
                  <Text size="sm" c="dimmed">
                    Returns accepted within 30 days of delivery. Item must be in original condition.
                    Contact the princess directly for return arrangements.
                  </Text>
                </div>
                {sellerLocation && (
                  <>
                    <Divider />
                    <div>
                      <Text fw={500} mb="xs">Shipping Location:</Text>
                      <Text size="sm" c="dimmed">
                        Ships from {sellerLocation}
                      </Text>
                    </div>
                  </>
                )}
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default ProductDetailPage;
